import * as React from 'react';
import { IgcHierarchicalGridComponent as Component } from 'igniteui-webcomponents-grids/grids/index.js';
import type { IgcHierarchicalGridComponentEventMap } from 'igniteui-webcomponents-grids/grids/index.js';
import { type EventName, createComponent } from '../react-props.js';

/**
 * Hierarchical grid
 * @fires columnsAutogenerated - undefined
 * @fires childrenResolved - Emitted when content children are resolved and collections in grid are updated.
 * @fires dataPreLoad - Emitted when a new chunk of data is loaded from virtualization.
 * @fires filteringExpressionsTreeChange - Emitted after filtering is performed.
 * @fires advancedFilteringExpressionsTreeChange - Emitted after advanced filtering is performed.
 * @fires gridScroll - Emitted when grid is scrolled horizontally/vertically.
 * @fires cellClick - Emitted when a cell is clicked.
 * @fires rowClick - Emitted when a row is clicked.
 * @fires formGroupCreated - Emitted when formGroup is created on edit of row/cell.
 * @fires validationStatusChange - Emitted when grid's validation status changes.
 * @fires selected - Emitted when a cell is selected.
 * @fires rowSelectionChanging - Emitted when grid row is selected.
 * @fires columnSelectionChanging - Emitted when column is selected.
 * @fires columnPin - Emitted before column is pinned.
 * @fires columnPinned - Emitted after column is pinned.
 * @fires cellEditEnter - Emitted when cell enters edit mode.
 * @fires cellEditExit - Emitted when cell exits edit mode.
 * @fires cellEdit - Emitted when cell has been edited.
 * @fires cellEditDone - Emitted after cell has been edited and editing has been committed.
 * @fires rowEditEnter - Emitted when a row enters edit mode.
 * @fires rowEdit - Emitted when exiting edit mode for a row.
 * @fires rowEditDone - Emitted after exiting edit mode for a row and editing has been committed.
 * @fires rowEditExit - Emitted when row editing is canceled.
 * @fires columnInit - Emitted when a column is initialized.
 * @fires sorting - Emitted before sorting expressions are applied.
 * @fires sortingDone - Emitted after sorting is completed.
 * @fires filtering - Emitted before filtering expressions are applied.
 * @fires filteringDone - Emitted after filtering is performed through the UI.
 * @fires rowAdded - Emitted when a row is added.
 * @fires rowDeleted - Emitted when a row is deleted.
 * @fires rowDelete - Emmited when deleting a row.
 * @fires rowAdd - Emmited just before the newly added row is commited.
 * @fires columnResized - Emitted after column is resized.
 * @fires contextMenu - Emitted when a cell or row is right clicked.
 * @fires doubleClick - Emitted when a cell is double clicked.
 * @fires columnVisibilityChanging - Emitted before column visibility is changed.
 * @fires columnVisibilityChanged - Emitted after column visibility is changed.
 * @fires columnMovingStart - Emitted when column moving starts.
 * @fires columnMoving - Emitted during the column moving operation.
 * @fires columnMovingEnd - Emitted when column moving ends.
 * @fires gridKeydown - Emitted when keydown is triggered over element inside grid's body.
 * @fires rowDragStart - Emitted when start dragging a row.
 * @fires rowDragEnd - Emitted when dropping a row.
 * @fires gridCopy - Emitted when a copy operation is executed.
 * @fires expansionStatesChange - Emitted when the rows are expanded or collapsed.
 * @fires selectedRowsChange - Emitted when the rows are selected or deselected.
 * @fires rowToggle - Emitted when the expanded state of a row gets changed.
 * @fires rowPinning - Emitted when the pinned state of a row is changed.
 * @fires rowPinned - Emitted when the pinned state of a row is changed.
 * @fires activeNodeChange - Emitted when the active node is changed.
 * @fires sortingExpressionsChange - Emitted before sorting is performed.
 * @fires toolbarExporting - Emitted when an export process is initiated by the user.
 * @fires rangeSelected - Emitted when making a range selection.
 * @fires rendered - Emitted after the ngAfterViewInit hook. At this point the grid exists in the DOM
 * @fires dataChanging - Emitted before the grid's data view is changed because of a data operation, rebinding, etc.
 * @fires dataChanged - Emitted after the grid's data view is changed because of a data operation, rebinding, etc.
 * @class
 */
export const IgrHierarchicalGrid = /* @__PURE__ */ createComponent({
  tagName: 'igc-hierarchical-grid',
  displayName: 'IgrHierarchicalGrid',
  elementClass: Component,
  react: React,
  moveBackOnDelete: true,
  events: {
    onColumnsAutogenerated: 'columnsAutogenerated' as EventName<
      IgcHierarchicalGridComponentEventMap['columnsAutogenerated']
    >,
    onChildrenResolved: 'childrenResolved' as EventName<
      IgcHierarchicalGridComponentEventMap['childrenResolved']
    >,
    onDataPreLoad: 'dataPreLoad' as EventName<
      IgcHierarchicalGridComponentEventMap['dataPreLoad']
    >,
    onFilteringExpressionsTreeChange:
      'filteringExpressionsTreeChange' as EventName<
        IgcHierarchicalGridComponentEventMap['filteringExpressionsTreeChange']
      >,
    onAdvancedFilteringExpressionsTreeChange:
      'advancedFilteringExpressionsTreeChange' as EventName<
        IgcHierarchicalGridComponentEventMap['advancedFilteringExpressionsTreeChange']
      >,
    onGridScroll: 'gridScroll' as EventName<
      IgcHierarchicalGridComponentEventMap['gridScroll']
    >,
    onCellClick: 'cellClick' as EventName<
      IgcHierarchicalGridComponentEventMap['cellClick']
    >,
    onRowClick: 'rowClick' as EventName<
      IgcHierarchicalGridComponentEventMap['rowClick']
    >,
    onFormGroupCreated: 'formGroupCreated' as EventName<
      IgcHierarchicalGridComponentEventMap['formGroupCreated']
    >,
    onValidationStatusChange: 'validationStatusChange' as EventName<
      IgcHierarchicalGridComponentEventMap['validationStatusChange']
    >,
    onSelected: 'selected' as EventName<
      IgcHierarchicalGridComponentEventMap['selected']
    >,
    onRowSelectionChanging: 'rowSelectionChanging' as EventName<
      IgcHierarchicalGridComponentEventMap['rowSelectionChanging']
    >,
    onColumnSelectionChanging: 'columnSelectionChanging' as EventName<
      IgcHierarchicalGridComponentEventMap['columnSelectionChanging']
    >,
    onColumnPin: 'columnPin' as EventName<
      IgcHierarchicalGridComponentEventMap['columnPin']
    >,
    onColumnPinned: 'columnPinned' as EventName<
      IgcHierarchicalGridComponentEventMap['columnPinned']
    >,
    onCellEditEnter: 'cellEditEnter' as EventName<
      IgcHierarchicalGridComponentEventMap['cellEditEnter']
    >,
    onCellEditExit: 'cellEditExit' as EventName<
      IgcHierarchicalGridComponentEventMap['cellEditExit']
    >,
    onCellEdit: 'cellEdit' as EventName<
      IgcHierarchicalGridComponentEventMap['cellEdit']
    >,
    onCellEditDone: 'cellEditDone' as EventName<
      IgcHierarchicalGridComponentEventMap['cellEditDone']
    >,
    onRowEditEnter: 'rowEditEnter' as EventName<
      IgcHierarchicalGridComponentEventMap['rowEditEnter']
    >,
    onRowEdit: 'rowEdit' as EventName<
      IgcHierarchicalGridComponentEventMap['rowEdit']
    >,
    onRowEditDone: 'rowEditDone' as EventName<
      IgcHierarchicalGridComponentEventMap['rowEditDone']
    >,
    onRowEditExit: 'rowEditExit' as EventName<
      IgcHierarchicalGridComponentEventMap['rowEditExit']
    >,
    onColumnInit: 'columnInit' as EventName<
      IgcHierarchicalGridComponentEventMap['columnInit']
    >,
    onSorting: 'sorting' as EventName<
      IgcHierarchicalGridComponentEventMap['sorting']
    >,
    onSortingDone: 'sortingDone' as EventName<
      IgcHierarchicalGridComponentEventMap['sortingDone']
    >,
    onFiltering: 'filtering' as EventName<
      IgcHierarchicalGridComponentEventMap['filtering']
    >,
    onFilteringDone: 'filteringDone' as EventName<
      IgcHierarchicalGridComponentEventMap['filteringDone']
    >,
    onRowAdded: 'rowAdded' as EventName<
      IgcHierarchicalGridComponentEventMap['rowAdded']
    >,
    onRowDeleted: 'rowDeleted' as EventName<
      IgcHierarchicalGridComponentEventMap['rowDeleted']
    >,
    onRowDelete: 'rowDelete' as EventName<
      IgcHierarchicalGridComponentEventMap['rowDelete']
    >,
    onRowAdd: 'rowAdd' as EventName<
      IgcHierarchicalGridComponentEventMap['rowAdd']
    >,
    onColumnResized: 'columnResized' as EventName<
      IgcHierarchicalGridComponentEventMap['columnResized']
    >,
    onContextMenu: 'contextMenu' as EventName<
      IgcHierarchicalGridComponentEventMap['contextMenu']
    >,
    onDoubleClick: 'doubleClick' as EventName<
      IgcHierarchicalGridComponentEventMap['doubleClick']
    >,
    onColumnVisibilityChanging: 'columnVisibilityChanging' as EventName<
      IgcHierarchicalGridComponentEventMap['columnVisibilityChanging']
    >,
    onColumnVisibilityChanged: 'columnVisibilityChanged' as EventName<
      IgcHierarchicalGridComponentEventMap['columnVisibilityChanged']
    >,
    onColumnMovingStart: 'columnMovingStart' as EventName<
      IgcHierarchicalGridComponentEventMap['columnMovingStart']
    >,
    onColumnMoving: 'columnMoving' as EventName<
      IgcHierarchicalGridComponentEventMap['columnMoving']
    >,
    onColumnMovingEnd: 'columnMovingEnd' as EventName<
      IgcHierarchicalGridComponentEventMap['columnMovingEnd']
    >,
    onGridKeydown: 'gridKeydown' as EventName<
      IgcHierarchicalGridComponentEventMap['gridKeydown']
    >,
    onRowDragStart: 'rowDragStart' as EventName<
      IgcHierarchicalGridComponentEventMap['rowDragStart']
    >,
    onRowDragEnd: 'rowDragEnd' as EventName<
      IgcHierarchicalGridComponentEventMap['rowDragEnd']
    >,
    onGridCopy: 'gridCopy' as EventName<
      IgcHierarchicalGridComponentEventMap['gridCopy']
    >,
    onExpansionStatesChange: 'expansionStatesChange' as EventName<
      IgcHierarchicalGridComponentEventMap['expansionStatesChange']
    >,
    onSelectedRowsChange: 'selectedRowsChange' as EventName<
      IgcHierarchicalGridComponentEventMap['selectedRowsChange']
    >,
    onRowToggle: 'rowToggle' as EventName<
      IgcHierarchicalGridComponentEventMap['rowToggle']
    >,
    onRowPinning: 'rowPinning' as EventName<
      IgcHierarchicalGridComponentEventMap['rowPinning']
    >,
    onRowPinned: 'rowPinned' as EventName<
      IgcHierarchicalGridComponentEventMap['rowPinned']
    >,
    onActiveNodeChange: 'activeNodeChange' as EventName<
      IgcHierarchicalGridComponentEventMap['activeNodeChange']
    >,
    onSortingExpressionsChange: 'sortingExpressionsChange' as EventName<
      IgcHierarchicalGridComponentEventMap['sortingExpressionsChange']
    >,
    onToolbarExporting: 'toolbarExporting' as EventName<
      IgcHierarchicalGridComponentEventMap['toolbarExporting']
    >,
    onRangeSelected: 'rangeSelected' as EventName<
      IgcHierarchicalGridComponentEventMap['rangeSelected']
    >,
    onRendered: 'rendered' as EventName<
      IgcHierarchicalGridComponentEventMap['rendered']
    >,
    onDataChanging: 'dataChanging' as EventName<
      IgcHierarchicalGridComponentEventMap['dataChanging']
    >,
    onDataChanged: 'dataChanged' as EventName<
      IgcHierarchicalGridComponentEventMap['dataChanged']
    >,
  },
  renderProps: {
    emptyGridTemplate: 'emptyGridTemplate',
    addRowEmptyTemplate: 'addRowEmptyTemplate',
    loadingGridTemplate: 'loadingGridTemplate',
    dragGhostCustomTemplate: 'dragGhostCustomTemplate',
    rowEditTextTemplate: 'rowEditTextTemplate',
    rowAddTextTemplate: 'rowAddTextTemplate',
    rowEditActionsTemplate: 'rowEditActionsTemplate',
    rowExpandedIndicatorTemplate: 'rowExpandedIndicatorTemplate',
    rowCollapsedIndicatorTemplate: 'rowCollapsedIndicatorTemplate',
    headerExpandedIndicatorTemplate: 'headerExpandedIndicatorTemplate',
    headerCollapsedIndicatorTemplate: 'headerCollapsedIndicatorTemplate',
    excelStyleHeaderIconTemplate: 'excelStyleHeaderIconTemplate',
    sortAscendingHeaderIconTemplate: 'sortAscendingHeaderIconTemplate',
    sortDescendingHeaderIconTemplate: 'sortDescendingHeaderIconTemplate',
    sortHeaderIconTemplate: 'sortHeaderIconTemplate',
    headSelectorTemplate: 'headSelectorTemplate',
    rowSelectorTemplate: 'rowSelectorTemplate',
    dragIndicatorIconTemplate: 'dragIndicatorIconTemplate',
  },
});

export type IgrHierarchicalGrid = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrHierarchicalGridModule = Component;
